import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { apiUrl } from '../api/client';
import { createApiClient } from '../api/ApiClient';
import { ChatMessageFeedback } from '../types/ChatMessageFeedback';
import type { ChatMessageDto } from '../types/ChatMessageDto';
import { ApiRoutes } from '../types/ApiRoutes';
import { FacebookShareButton } from 'react-share';
export interface MessageFeedbackProps {
  /** The chat message this feedback is for */
  message: ChatMessageDto;
  /** Optional callback when feedback is submitted */
  onFeedbackSubmit?: (messageId: string, feedback: ChatMessageFeedback | null) => void;
  /** Optional callback when favorite is toggled */
  onFavoriteToggle?: (messageId: string, isFavorite: boolean) => void;
  /** Optional callback when share is toggled */
  onShareToggle?: (messageId: string, isShared: boolean, shareLink?: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * MessageFeedback component allows users to provide thumbs up/down feedback
 * on assistant messages and mark them as favorites.
 */
const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  message,
  onFeedbackSubmit,
  onFavoriteToggle,
  onShareToggle,
  className = '',
}) => {
  const [feedback, setFeedback] = useState<ChatMessageFeedback | null>(
    message.messageFeedback ?? null
  );
  const [isFavorite, setIsFavorite] = useState<boolean>(message.isFavorite ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingFavorite, setSubmittingFavorite] = useState(false);
  const [isShared, setIsShared] = useState<boolean>(message.isShared ?? false);
  const [shareLink, setShareLink] = useState<string | null>(
    message.messageId
      ? `${window.location.origin}/shares/msg/` + message.messageId.replace(/-/g, '')
      : null
  );
  const [enableFacebookShare] = useState<boolean>(true);
  const [submittingShare, setSubmittingShare] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const { authorizedFetch } = useAuth();
  const api = useMemo(() => createApiClient(authorizedFetch), [authorizedFetch]);

  // Update internal state when prop changes
  useEffect(() => {
    setFeedback(message.messageFeedback ?? null);
  }, [message.messageFeedback]);

  useEffect(() => {
    setIsFavorite(message.isFavorite ?? false);
  }, [message.isFavorite]);

  const handleFeedback = async (feedbackValue: ChatMessageFeedback) => {
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);

      // If clicking the same feedback button, clear the feedback
      if (feedback === feedbackValue) {
        await api.delete(apiUrl(`${ApiRoutes.Messages}/${message.messageId}/feedback`), {
          errorMessage: 'Failed to clear feedback',
        });
        setFeedback(null);
        onFeedbackSubmit?.(message.messageId, null);
      } else {
        // Otherwise, set new feedback
        const endpoint = feedbackValue === ChatMessageFeedback.Positive ? 'positive' : 'negative';
        await api.post(
          apiUrl(`${ApiRoutes.Messages}/${message.messageId}/feedback/${endpoint}`),
          {},
          { errorMessage: 'Failed to submit feedback' }
        );
        setFeedback(feedbackValue);
        onFeedbackSubmit?.(message.messageId, feedbackValue);
      }
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFavoriteToggle = async () => {
    if (submittingFavorite) {
      return;
    }

    try {
      setSubmittingFavorite(true);

      if (isFavorite) {
        // Remove favorite
        await api.delete(apiUrl(`${ApiRoutes.Messages}/${message.messageId}/favorite`), {
          errorMessage: 'Failed to remove favorite',
        });
        setIsFavorite(false);
        onFavoriteToggle?.(message.messageId, false);
      } else {
        // Add favorite
        await api.post(
          apiUrl(`${ApiRoutes.Messages}/${message.messageId}/favorite`),
          {},
          { errorMessage: 'Failed to add favorite' }
        );
        setIsFavorite(true);
        onFavoriteToggle?.(message.messageId, true);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    } finally {
      setSubmittingFavorite(false);
    }
  };

  const handleCopyLink = async () => {
    if (submittingShare) {
      return;
    }

    try {
      setSubmittingShare(true);

      // If not already shared, create share link first
      if (!isShared || !shareLink) {
        const response = await api.post<{ shareLink: string }>(
          apiUrl(`${ApiRoutes.Messages}/${message.messageId}/share`),
          {},
          { errorMessage: 'Failed to share message' }
        );
        setIsShared(true);
        setShareLink(response.shareLink);
        onShareToggle?.(message.messageId, true, response.shareLink);

        // Show modal with the new link
        setShowShareModal(true);
      } else {
        // Show modal with existing link
        setShowShareModal(true);
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
    } finally {
      setSubmittingShare(false);
      setShowShareDropdown(false);
    }
  };

  const handleModalCopyLink = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        // Optionally show a success message
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  const handleStartSharing = async () => {
    if (submittingShare) {
      return;
    }

    try {
      setSubmittingShare(true);

      // If not already shared, create share link first
      if (!isShared || !shareLink) {
        const response = await api.post<{ shareLink: string }>(
          apiUrl(`${ApiRoutes.Messages}/${message.messageId}/share`),
          {},
          { errorMessage: 'Failed to share message' }
        );
        setIsShared(true);
        setShareLink(response.shareLink);
        onShareToggle?.(message.messageId, true, response.shareLink);
      }
    } catch (err) {
      console.error('Failed to enable sharing:', err);
    } finally {
      setSubmittingShare(false);
    }
  };

  const handleStopSharing = async () => {
    if (submittingShare) {
      return;
    }

    try {
      setSubmittingShare(true);

      await api.delete(apiUrl(`${ApiRoutes.Messages}/${message.messageId}/share`), {
        errorMessage: 'Failed to stop sharing',
      });
      setIsShared(false);
      setShareLink(null);
      onShareToggle?.(message.messageId, false);
    } catch (err) {
      console.error('Failed to stop sharing:', err);
    } finally {
      setSubmittingShare(false);
      setShowShareDropdown(false);
    }
  };

  const handleFacebookShare = async () => {
    if (submittingShare) {
      return;
    }

    try {
      setSubmittingShare(true);

      // If not already shared, create share link first
      if (!isShared || !shareLink) {
        const response = await api.post<{ shareLink: string }>(
          apiUrl(`${ApiRoutes.Messages}/${message.messageId}/share`),
          {},
          { errorMessage: 'Failed to share message' }
        );
        setIsShared(true);
        setShareLink(response.shareLink);
        onShareToggle?.(message.messageId, true, response.shareLink);

        // Show Facebook share window with new link
        const url = encodeURIComponent(response.shareLink);
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${url}`,
          '_blank',
          'noopener,noreferrer'
        );
      } else {
        // Show Facebook share window with existing link
        const url = encodeURIComponent(shareLink);
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${url}`,
          '_blank',
          'noopener,noreferrer'
        );
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
    } finally {
      setSubmittingShare(false);
      setShowShareDropdown(false);
    }
  };

  return (
    <>
      <div className={`message-feedback d-inline ${className}`.trim()}>
        <button
          type="button"
          className={`btn btn-link btn-outline-light`}
          onClick={() => handleFeedback(ChatMessageFeedback.Positive)}
          disabled={submitting}
          aria-label={feedback === ChatMessageFeedback.Positive ? 'Clear thumbs up' : 'Thumbs up'}
          title={feedback === ChatMessageFeedback.Positive ? 'Clear feedback' : 'Helpful response'}
        >
          <i
            className={`bi bi-hand-thumbs-up${feedback === ChatMessageFeedback.Positive ? '-fill' : ''}`}
          ></i>
        </button>
        <button
          type="button"
          className={`btn btn-link btn-outline-light`}
          onClick={() => handleFeedback(ChatMessageFeedback.Negative)}
          disabled={submitting}
          aria-label={
            feedback === ChatMessageFeedback.Negative ? 'Clear thumbs down' : 'Thumbs down'
          }
          title={feedback === ChatMessageFeedback.Negative ? 'Clear feedback' : 'Not helpful'}
        >
          <i
            className={`bi bi-hand-thumbs-down${feedback === ChatMessageFeedback.Negative ? '-fill' : ''}`}
          ></i>
        </button>
        <button
          type="button"
          className={`btn btn-link text-danger`}
          onClick={handleFavoriteToggle}
          disabled={submittingFavorite}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <i className={`bi bi-heart${isFavorite ? '-fill' : ''}`}></i>
        </button>
        <div className="btn-group">
          <button
            type="button"
            className={`btn btn-link btn-outline-light`}
            onClick={() => setShowShareDropdown(!showShareDropdown)}
            disabled={submittingShare}
            aria-label="Share options"
            title="Share this message"
            aria-expanded={showShareDropdown}
          >
            <i className={`bi bi-share${isShared ? '-fill' : ''}`}></i>
          </button>
          {showShareDropdown && (
            <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute' }}>
              <li>
                <button
                  className="dropdown-item"
                  type="button"
                  onClick={handleCopyLink}
                  disabled={submittingShare}
                >
                  <i className="bi bi-clipboard me-2"></i>
                  Copy link
                </button>
              </li>
              {enableFacebookShare && (
                <>
                  <li>
                    <button className="dropdown-item" type="button" disabled={submittingShare}>
                      <FacebookShareButton
                        url={shareLink ?? ''}
                        hashtag="#WalkItWithMe"
                        beforeOnClick={handleStartSharing}
                      >
                        <i className="bi bi-facebook me-2"></i>
                        Share to Facebook (via button)
                      </FacebookShareButton>
                    </button>
                  </li>
                </>
              )}
              {enableFacebookShare && (
                <>
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={handleFacebookShare}
                      disabled={submittingShare}
                    >
                      <i className="bi bi-facebook me-2"></i>
                      Share to Facebook (via popup)
                    </button>
                  </li>
                </>
              )}
              {isShared && (
                <>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      type="button"
                      onClick={handleStopSharing}
                      disabled={submittingShare}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Stop sharing
                    </button>
                  </li>
                </>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {showShareDropdown && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}
          onClick={() => setShowShareDropdown(false)}
        />
      )}

      {/* Share Link Modal */}
      {showShareModal && shareLink && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowShareModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Share Link</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowShareModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>Share this message using the link below:</p>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={shareLink}
                    readOnly
                    aria-label="Share link"
                  />
                  <button className="btn btn-primary" type="button" onClick={handleModalCopyLink}>
                    <i className="bi bi-clipboard"></i> Copy
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowShareModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageFeedback;
